import {
  Box,
  Typography,
  Stack,
  Select,
  MenuItem,
  TextField,
  Popper,
  IconButton,
  InputAdornment,
} from "@mui/material";
import Autocomplete, { autocompleteClasses } from "@mui/material/Autocomplete";
import { styled } from "@mui/material/styles";
import { Add, Remove } from "@mui/icons-material";
import Alert from "@mui/material/Alert";
import ScrollToTop from "../../../components/nav/ScrollToTop";
import { Controller, useFormContext } from "react-hook-form";
import {
  employmentStatuses,
  employmentStatusList,
  employmentTitleIdMap,
  employmentTypeIdMap,
} from "../domain/entities/employment";
import { homeStatuses } from "../domain/entities/home";
import { buildHousehold } from "../domain/entities/household";
import { useOccupationSelectionQuery } from "../queries/useOccupationSelectionQuery";
import { ModeInfo } from "../domain/entities/mode";
import { ListboxComponent, StyledNumericFormat } from "./common";
import { FinancialStatementView } from "../api/dto/FinancialStatement";

interface AboutYouProps {
  financialData: FinancialStatementView | null | undefined;
  mode?: ModeInfo;
}

function AboutYou({ financialData }: Readonly<AboutYouProps>) {
  const MAX_PERSON_LIMIT = 20;

  const { control, setValue } = useFormContext();

  const occupationQuery = useOccupationSelectionQuery();

  const buildHouseHoldArr = buildHousehold(financialData);

  const handleHouseholdChange = (name: string, value: number) => {
    const itemMeta = (buildHouseHoldArr ?? []).find((h) => h.id === name);

    if (!itemMeta) return;

    const min = itemMeta.minValue ?? (name === "adults" ? 1 : 0);
    const max = itemMeta.maxValue ?? MAX_PERSON_LIMIT;
    const next = Math.max(Math.min(value ?? 0, max), min);

    setValue(`aboutYou.${name}`, next, {
      shouldValidate: true,
      shouldDirty: true,
    });
  };

  const noSfsMessage = `Hey there! It looks like you haven't completed a Financial Statement with us. 
    Before you can set up a repayment plan we firstly need to make sure that it is affordable for you. 
    Please complete the form below.`;

  const StyledPopper = styled(Popper)({
    [`& .${autocompleteClasses.listbox}`]: {
      boxSizing: "border-box",
      "& ul": {
        padding: 0,
        margin: 0,
      },
    },
  });

  if (occupationQuery?.isError) {
    return (
      <Alert variant="filled" severity="error" sx={{ color: "#fff" }}>
        {occupationQuery.error?.message ?? "An error occurred."}
      </Alert>
    );
  }

  return (
    <>
      <ScrollToTop />
      {financialData?.mode?.monthsSinceReview === null && (
        <Alert variant="filled" severity="warning" sx={{ color: "#fff" }}>
          {noSfsMessage}
        </Alert>
      )}
      <Typography fontWeight="bold" paddingTop={5} component="p">
        Income & Expenditure
      </Typography>
      <Typography paddingBottom={2} component="p">
        Explore financial peace with our user-friendly Income & Expenditure
        form. Please fill out the information below to start.
      </Typography>
      <Typography paddingBottom={2} component="p">
        You may find that having your recent bills & transaction history to hand
        may help you complete the form.
      </Typography>

      <Stack sx={{ mt: 2 }} spacing={2} direction={{ xs: "column", md: "row" }}>
        <Box sx={{ width: { xs: "100%", md: "50%" } }}>
          {/* Left column */}
          <Box id="employment-radio">
            <Typography
              component="label"
              id="employment-status-label"
              htmlFor="employment-status-select"
              sx={{
                color: "#000000",
                fontWeight: "bold",
                pb: 3,
                display: "block",
              }}
            >
              <strong>Employment status</strong>
            </Typography>
            <Controller
              name="aboutYou.customersEmploymentStatus"
              control={control}
              defaultValue={"UNK"}
              render={({ field }) => (
                <Select
                  labelId="employment-status-label"
                  id="employment-status-select"
                  fullWidth
                  size="small"
                  displayEmpty
                  sx={{ borderRadius: "0px", backgroundColor: "#FFF" }}
                  value={field.value ?? "UNK"}
                  onChange={(event) => field.onChange(event.target.value)}
                  aria-labelledby="employment-status-label employment-status"
                  renderValue={(selected) =>
                    selected === "" ? (
                      <Typography>Please select</Typography>
                    ) : (
                      employmentStatuses[selected]
                    )
                  }
                >
                  {employmentStatusList.map(({ code, label }) => (
                    <MenuItem key={code} value={code}>
                      {label}
                    </MenuItem>
                  ))}
                </Select>
              )}
            />
          </Box>

          <Box sx={{ width: "100%", pb: 3 }}>
            <Typography
              id="employment-title-label"
              sx={{
                color: "#000000",
                fontWeight: "bold",
                py: 3,
                display: "block",
              }}
            >
              Employment Title
            </Typography>
            <Controller
              name="aboutYou.employmentTitleId"
              control={control}
              defaultValue={employmentTitleIdMap.Unknown}
              render={({ field }) => (
                <Autocomplete
                  id="employment-title-select"
                  sx={{ width: "100%", backgroundColor: "#FFF" }}
                  disableListWrap
                  PopperComponent={StyledPopper}
                  ListboxComponent={ListboxComponent as any}
                  options={occupationQuery.data?.joined ?? []}
                  value={
                    occupationQuery.data?.joined?.find(
                      (title) => title.occupationTitleId === field.value,
                    ) || null
                  }
                  onChange={(_, newValue) => {
                    field.onChange(
                      newValue?.occupationTitleId ||
                        employmentTitleIdMap.Unknown,
                    );
                    // Also set the corresponding occupationTypeId based on the selected title
                    setValue(
                      "aboutYou.employmentTypeId",
                      newValue?.occupationTypeId ?? employmentTypeIdMap.Unknown,
                      { shouldDirty: true },
                    );
                  }}
                  groupBy={(option) =>
                    `${occupationQuery.data?.getTypeLabelById(option.occupationTypeId).substring(0, 60)}...`
                  }
                  getOptionLabel={(option) => option.occupationTitle}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Please select an employment title"
                    />
                  )}
                  renderOption={(props, option) => (
                    <li {...props} key={option.occupationTitleId}>
                      {option.occupationTitle}
                    </li>
                  )}
                  renderGroup={(params) => <li {...params} />}
                />
              )}
            />
          </Box>

          <Box sx={{ pb: 2 }}>
            <Typography
              component="label"
              id="partner-employment-label"
              htmlFor="partner-employment-select"
              sx={{
                color: "#000000",
                fontWeight: "bold",
                pb: 3,
                display: "block",
              }}
            >
              <strong>Partner's Employment status</strong> <i>(optional)</i>
            </Typography>
            <Controller
              name="aboutYou.partnersEmploymentStatus"
              control={control}
              defaultValue={"UNK"}
              render={({ field }) => (
                <Select
                  labelId="partner-employment-label"
                  id="partner-employment-select"
                  fullWidth
                  size="small"
                  displayEmpty
                  sx={{ borderRadius: "0px", backgroundColor: "#FFF" }}
                  value={field.value ?? "UNK"}
                  onChange={(event) => field.onChange(event.target.value)}
                  aria-labelledby="partner-employment-label partner-employment"
                  renderValue={(selected) =>
                    selected === "" ? (
                      <Typography>Please select</Typography>
                    ) : (
                      employmentStatuses[selected]
                    )
                  }
                >
                  {Object.keys(employmentStatuses).map((item) => (
                    <MenuItem key={item.toUpperCase()} value={item}>
                      {employmentStatuses[item]}
                    </MenuItem>
                  ))}
                </Select>
              )}
            />
          </Box>
        </Box>

        <Box sx={{ width: { xs: "100%", md: "50%" } }}>
          {/* Right column */}

          <Box sx={{ pb: 3 }}>
            <Typography
              component="label"
              id="home-status-label"
              htmlFor="home-status-select"
              sx={{
                color: "#000000",
                fontWeight: "bold",
                pb: 3,
                display: "block",
              }}
            >
              Home Status
            </Typography>
            <Controller
              name="aboutYou.homeStatus"
              control={control}
              defaultValue={""}
              render={({ field }) => (
                <Select
                  labelId="home-status-label"
                  id="home-status-select"
                  fullWidth
                  size="small"
                  displayEmpty
                  sx={{ borderRadius: "0px", backgroundColor: "#FFF" }}
                  value={field.value ?? ""}
                  onChange={(event) => field.onChange(event.target.value)}
                  aria-labelledby="home-status-label home-status"
                  renderValue={(selected) =>
                    selected === "" ? (
                      <Typography>Please select</Typography>
                    ) : (
                      homeStatuses[selected]
                    )
                  }
                >
                  {Object.keys(homeStatuses).map((item) => (
                    <MenuItem key={item.toUpperCase()} value={item}>
                      {homeStatuses[item]}
                    </MenuItem>
                  ))}
                </Select>
              )}
            />
          </Box>

          <Box>
            <Typography id="household-label" sx={{ fontWeight: "bold", pb: 2 }}>
              Household
            </Typography>

            {buildHouseHoldArr?.map((item, idx) => (
              <Box key={item.id} sx={{ display: "flex", pb: 2, width: "100%" }}>
                <Typography
                  component="label"
                  id={`${item.id}-label`}
                  htmlFor={`${item.id}`}
                  sx={{ width: "70%", display: "inline", pt: 1 }}
                >
                  {item.label}
                </Typography>

                <Controller
                  name={`aboutYou.${item.id}`}
                  control={control}
                  defaultValue={item.value}
                  render={({ field }) => (
                    <StyledNumericFormat
                      // @ts-ignore
                      customInput={TextField}
                      fullWidth
                      InputProps={{
                        style: { borderRadius: 0 },
                        startAdornment: (
                          <InputAdornment position="start">
                            <IconButton
                              onClick={() =>
                                handleHouseholdChange(
                                  item.id,
                                  (field.value ?? item.value) - 1,
                                )
                              }
                              disabled={
                                idx === 0
                                  ? (field.value ?? item.value) <= 1
                                  : (field.value ?? item.value) <= 0
                              }
                            >
                              <Remove />
                            </IconButton>
                          </InputAdornment>
                        ),
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              onClick={() =>
                                handleHouseholdChange(
                                  item.id,
                                  (field.value ?? item.value) + 1,
                                )
                              }
                              disabled={
                                (field.value ?? item.value) >= item.maxValue
                              }
                            >
                              <Add />
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                      value={field.value ?? item.value}
                      displayType="input"
                      allowNegative={false}
                      isAllowed={({ floatValue }) => {
                        const v = floatValue ?? 0;
                        return idx === 0 ? v >= 1 : v >= 0;
                      }}
                      onValueChange={({ floatValue }) =>
                        handleHouseholdChange(item.id, floatValue ?? 0)
                      }
                    />
                  )}
                />
              </Box>
            ))}
          </Box>
        </Box>
      </Stack>
    </>
  );
}

export default AboutYou;
